import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Calendar, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`)
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-list-page">
      <Helmet>
        <title>KoKo Sites Blog — Web Design, Tips & Case Studies from India</title>
        <meta
          name="description"
          content="Insights, guides, and case studies on web design, SEO, and running a digital business in India. From KoKo Sites, Bolpur Santiniketan."
        />
        <link rel="canonical" href="https://kokosites.in/blog" />
      </Helmet>
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <p className="section-label text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-red-600 justify-center">
            Notes From The Studio
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight mt-3">
            Blog &amp; <span className="text-red-600">Insights</span>
          </h1>
          <p className="text-sm text-zinc-500 mt-3 max-w-xl mx-auto">
            Thoughts on web design, SEO, building online businesses in India — written by Kamran &amp; the team.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-72 border border-white/10 bg-[#111] skeleton" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="border border-white/10 bg-[#111] p-12 text-center">
            <p className="text-zinc-500 text-sm">Nothing published yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block border border-white/10 bg-[#111] hover:border-red-600/70 hover:-translate-y-2 hover:shadow-[8px_8px_0_0_rgba(229,9,20,0.85)] transition-all duration-300"
              >
                {p.cover_image_url ? (
                  <div className="aspect-[16/9] overflow-hidden bg-[#1a1a1a]">
                    <img
                      src={p.cover_image_url}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-red-950/40 to-[#0A0A0A] flex items-center justify-center">
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-red-600/60">
                      KoKo Sites
                    </span>
                  </div>
                )}
                <div className="p-5 sm:p-6">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={10} /> {fmt(p.published_at || p.created_at)}
                    </span>
                  </div>
                  <h2 className="text-base sm:text-lg font-black uppercase tracking-tight text-white group-hover:text-red-500 transition-colors mb-2 line-clamp-2">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed line-clamp-3 mb-4">
                      {p.excerpt}
                    </p>
                  )}
                  {p.tags && p.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {p.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 text-red-500 border border-red-600/30"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="link-underline inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-red-600">
                    Read post <ArrowRight size={10} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
